import ApiService from '@/api';
import {
  CreateGroupModel,
  CreateQueueModel,
  DeleteGroupEventResponseModel,
  DeleteQueueInfoModel,
  GroupCreateEventResponseModel,
  GroupUpdateNameEventResponseModel,
  NamespaceModel,
  QueueTasksModel,
  ResponseModel,
  UpdateGroupNameModel,
  UpdateNamespaceNameModel,
  UpdateQueueNameModel,
} from '../models';
import {
  mapCreateGroupRequest,
  mapCreateQueueRequest,
  mapDeleteGroupRequest,
  mapDeleteNamespaceRequest,
  mapDeleteQueueRequest,
  mapGetTreeRequest,
  mapGetTreeResponse,
  mapUpdateGroupName,
  mapUpdateNamespaceName,
  mapUpdateQueueName,
  mapGetNamespaceAdminsResponse,
  mapGetNamespaceAdminsRequest,
  mapGetMyTeamCountEmployeesRequest,
} from './AdapterServiceMappers';
import MoveGroupRequest from '@/api/Main/dataSource/api/projects/groups/i-service/model/MoveGroupRequest';
import MoveQueueTaskRequest from '@/api/Main/dataSource/api/projects/groups/i-service/model/queue-tasks/MoveQueueTaskRequest';
import { TirPmNotificationEventBus, TirPmNotificationModel } from 'tir-components';
import { UserModel } from '@/pages/namespace-team-page/models';
import { CurrentUser } from 'tir-pm-user-core';
import { Events } from 'tir-pm-toolkit';
import { CreateNamespaceInfo, DeleteNamespaceInfo, UpdateNamespaceInfo } from '../types';
import { ProjectModel } from '@/models';
import EnumProjectType from '../enums/EnumProjectType';
import ProjectSearchRequest from '@/api/Main/dataSource/api/projects/base/i-service/models/ProjectSearchRequest';

export class AdapterService {
  private readonly api = new ApiService();

  /**Событие создания неймспейса */
  CreateNamespaceEvent = new Events<CreateNamespaceInfo>();
  /**Событие удаления неймспейса */
  DeleteNamespaceEvent = new Events<DeleteNamespaceInfo>();
  /**Событие обновления неймспейса */
  UpdateNamespaceEvent = new Events<UpdateNamespaceInfo>();
  /**Событие обновления очереди */
  UpdateQueueEvent = new Events<UpdateQueueNameModel>();
  /**Событие удаления очереди */
  DeleteQueueEvent = new Events<DeleteQueueInfoModel>();
  /**Событие создания очереди */
  CreateQueueEvent = new Events<QueueTasksModel>();
  /**Событие создания папки */
  OnCreateGroupEvent = new Events<GroupCreateEventResponseModel>();
  /**Событие удаления папки */
  OnDeleteGroupEvent = new Events<DeleteGroupEventResponseModel>();
  /**Событие обновления названия папки */
  OnUpdateNameGroupEvent = new Events<GroupUpdateNameEventResponseModel>();

  constructor() {
    this.api.Main.ProjectNamespaceService.OnCreateNamespaceEventEvent.on((resp) => {
      if (resp.IsSuccess && resp.Value) {
        this.CreateNamespaceEvent.trigger({
          ProjectId: resp.Value.ProjectId,
          Id: resp.Value.Id,
          Name: resp.Value.Name,
          IconId: resp.Value.SettingIconId,
          IconUrl: resp.Value.IconUrl,
          ColorId: resp.Value.SettingColorId,
          Color: resp.Value.Color,
        });
      }
    });

    this.api.Main.ProjectNamespaceService.DeleteEvent.on((resp) => {
      if (resp.IsSuccess && resp.Value) {
        this.DeleteNamespaceEvent.trigger({
          NamespaceId: String(resp.Value),
        });
      }
    });
    this.api.Main.ProjectNamespaceService.OnDeleteNamespaceEventEvent.on((resp) => {
      if (resp.IsSuccess && resp.Value) {
        this.DeleteNamespaceEvent.trigger({
          NamespaceId: resp.Value.NamespaceId,
        });
      }
    });

    this.api.Main.ProjectNamespaceService.OnUpdateNamespaceEventEvent.on((resp) => {
      if (resp.IsSuccess && resp.Value) {
        this.UpdateNamespaceEvent.trigger({
          Id: resp.Value.Id,
          Name: resp.Value.Name,
          IconId: resp.Value.IconId,
          IconUrl: resp.Value.IconUrl,
          ColorId: resp.Value.ColorId,
          Color: resp.Value.Color,
        });
      }
    });

    this.api.Main.ProjectQueueTaskService.OnUpdateNameQueueTaskEventEvent.on((resp) => {
      if (resp.IsSuccess && resp.Value) {
        this.UpdateQueueEvent.trigger({
          QueueId: resp.Value.Id,
          Name: resp.Value.Name,
        });
      }
    });

    this.api.Main.ProjectQueueTaskService.OnDeleteQueueTaskEventEvent.on((resp) => {
      if (resp.IsSuccess && resp.Value) {
        this.DeleteQueueEvent.trigger({
          QueueId: resp.Value.QueueTaskId,
        });
      }
    });

    this.api.Main.ProjectQueueTaskService.OnCreateQueueTaskEventEvent.on((resp) => {
      if (resp.IsSuccess && resp.Value) {
        this.DeleteQueueEvent.trigger({
          QueueId: resp.Value.Id,
        });
      }
    });
    this.api.Main.ProjectGroupService.OnCreateGroupEventEvent.on((resp) => {
      if (resp.IsSuccess && resp.Value) {
        this.OnCreateGroupEvent.trigger(resp.Value);
      }
    });
    this.api.Main.ProjectGroupService.OnDeleteGroupEventEvent.on((resp) => {
      if (resp.IsSuccess && resp.Value) {
        this.OnDeleteGroupEvent.trigger(resp.Value);
      }
    });
    this.api.Main.ProjectGroupService.OnUpdateNameGroupEventEvent.on((resp) => {
      if (resp.IsSuccess && resp.Value) {
        this.OnUpdateNameGroupEvent.trigger(resp.Value);
      }
    });
  }

  /**
   * * Получить список админов неймспейса
   * @param namespaceId ID неймспейса
   * @returns
   */
  getNamespaceAdmins(namespaceId: string) {
    return new Promise<ResponseModel<Array<UserModel>>>(async (resolve) => {
      const result = new ResponseModel<Array<UserModel>>();

      await this.api.Main.ProjectNamespaceService.getNamespaceByIdAsync(
        mapGetNamespaceAdminsRequest(namespaceId),
      )
        .then((resp) => {
          result.Value = mapGetNamespaceAdminsResponse(resp);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR getNamespaceAdmins', e);
          return resolve(result);
        });
    });
  }

  /**Получить список проектов */
  async GetProjects(value: string = ''): Promise<ResponseModel<ProjectModel[]>> {
    const result = new ResponseModel<ProjectModel[]>();

    try {
      const response = await this.api.Main.ProjectBaseService.searchAsync(
        new ProjectSearchRequest({ Name: value, Status: [EnumProjectType.Active] }),
      );
      result.Value = response.Projects.map((x) => new ProjectModel(x as any));
    } catch (e) {
      result.IsSuccess = false;
      result.ErrorCode = e?.ErrorCode;
      result.ErrorMessage = e?.ErrorMessage;
      console.error('ERROR ProjectBaseService.Search', e);
    }

    return result;
  }
  /**
   * * Получить дерево неймспейсов папок и очередей
   * @param projectId ID проекта
   * @returns
   */
  getTree(projectId: number, onlyNamespaces?: boolean) {
    return new Promise<ResponseModel<Array<NamespaceModel>>>(async (resolve) => {
      const result = new ResponseModel<Array<NamespaceModel>>();
      const request = mapGetTreeRequest(projectId) as any;
      const query = onlyNamespaces
        ? this.api.Main.ProjectNamespaceService.getListAsync(request as any)
        : this.api.Main.ProjectNamespaceService.getTreeAsync(request);

      await query
        .then((resp) => {
          const response = onlyNamespaces ? { Namespaces: resp.Items } : resp;
          result.Value = mapGetTreeResponse(response);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR getTree', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Создать группу
   * @param request
   * @returns
   */
  createGroup(request: CreateGroupModel) {
    return new Promise<ResponseModel<number>>(async (resolve) => {
      const result = new ResponseModel<number>();

      await this.api.Main.ProjectGroupService.createGroupAsync(mapCreateGroupRequest(request))
        .then((resp) => {
          result.Value = resp.GroupId;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR createGroup', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Создать очередь
   * @param request
   * @returns
   */
  createQueue(request: CreateQueueModel) {
    return new Promise<ResponseModel<string>>(async (resolve) => {
      const result = new ResponseModel<string>();

      await this.api.Main.ProjectQueueTaskService.createAsync(mapCreateQueueRequest(request))
        .then((resp) => {
          result.Value = resp.Id;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR createQueue', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Удалить неймспейс
   * @param namespaceId ID неймспейса
   * @param namespaceName Name неймспейса
   * @returns
   */
  deleteNamespace(namespaceId: string, namespaceName: string) {
    return new Promise<ResponseModel>(async (resolve) => {
      const result = new ResponseModel();

      await this.api.Main.ProjectNamespaceService.deleteAsync(
        mapDeleteNamespaceRequest(namespaceId),
      )
        .then((resp) => {
          result.IsSuccess = true;
          return resolve(result);
        })
        .catch((e) => {
          TirPmNotificationEventBus.emit(
            'add',
            new TirPmNotificationModel({
              title: `Рабочая область ${namespaceName} не пуста. Удаление невозможно`,
              type: 'error',
              group: 'general',
              life: 5000,
              closable: true,
            }),
          );
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR deleteNamespace', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Удалить группу
   * @param groupId ID группы
   * @param groupName Name группы
   * @returns
   */
  deleteGroup(groupId: number, groupName: string) {
    return new Promise<ResponseModel>(async (resolve) => {
      const result = new ResponseModel();

      await this.api.Main.ProjectGroupService.removeAsync(mapDeleteGroupRequest(groupId))
        .then((resp) => {
          result.IsSuccess = true;
          return resolve(result);
        })
        .catch((e) => {
          TirPmNotificationEventBus.emit(
            'add',
            new TirPmNotificationModel({
              title: `Папка ${groupName} не пуста. Удаление невозможно`,
              type: 'error',
              group: 'general',
              life: 5000,
              closable: true,
            }),
          );
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR deleteGroup', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Удалить очередь
   * @param queueId ID очереди
   * @param queueName Name очереди
   * @returns
   */
  deleteQueue(queueId: string, queueName) {
    return new Promise<ResponseModel>(async (resolve) => {
      const result = new ResponseModel();
      await this.api.Main.ProjectQueueTaskService.deleteAsync(mapDeleteQueueRequest(queueId))
        .then((resp) => {
          result.IsSuccess = true;
          return resolve(result);
        })
        .catch((e) => {
          TirPmNotificationEventBus.emit(
            'add',
            new TirPmNotificationModel({
              title: `Очередь ${queueName} не пуста. Удаление невозможно`,
              type: 'error',
              group: 'general',
              life: 5000,
              closable: true,
            }),
          );
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR deleteQueue', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Обновить название группы
   * @param request
   * @returns
   */
  updateGroupName(request: UpdateGroupNameModel) {
    return new Promise<ResponseModel>(async (resolve) => {
      const result = new ResponseModel();

      await this.api.Main.ProjectGroupService.updateNameAsync(mapUpdateGroupName(request))
        .then((resp) => {
          result.IsSuccess = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR updateGroupName', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Обновить название очереди задач
   * @param request
   * @returns
   */
  updateQueueName(request: UpdateQueueNameModel) {
    return new Promise<ResponseModel>(async (resolve) => {
      const result = new ResponseModel();

      await this.api.Main.ProjectQueueTaskService.updateNameAsync(mapUpdateQueueName(request))
        .then((resp) => {
          result.IsSuccess = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR updateQueueName', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Обновить название неймспейса
   * @param request ID запроса
   * @returns
   */
  updateNamespaceName(request: UpdateNamespaceNameModel) {
    return new Promise<ResponseModel>(async (resolve) => {
      const result = new ResponseModel();

      await this.api.Main.ProjectNamespaceService.updateNameAsync(mapUpdateNamespaceName(request))
        .then((resp) => {
          result.IsSuccess = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR updateNamespaceName', e);
          return resolve(result);
        });
    });
  }
  /**Перемещение группы */
  moveGroup(groupId: number, parentId: number | string) {
    return new Promise<ResponseModel>(async (resolve) => {
      const result = new ResponseModel();
      const request = new MoveGroupRequest({
        GroupId: groupId,
      });

      if (typeof parentId == 'string') {
        request.ParentNamespaceId = parentId;
      } else if (typeof parentId == 'number') {
        request.ParentGroupId = parentId;
      }

      await this.api.Main.ProjectGroupService.moveGroupAsync(request)
        .then((resp) => {
          result.IsSuccess = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR moveGroup', e);
          return resolve(result);
        });
    });
  }
  /**Перемешение очереди */
  moveQueue(queueId: string, parentId: number | string) {
    return new Promise<ResponseModel>(async (resolve) => {
      const result = new ResponseModel();

      const request = new MoveQueueTaskRequest({
        QueueTaskId: queueId,
      });

      if (typeof parentId == 'string') {
        request.ParentNamespaceId = parentId;
      } else if (typeof parentId == 'number') {
        request.ParentGroupId = parentId;
      }

      await this.api.Main.ProjectQueueTaskService.moveQueueTaskAsync(request)
        .then((resp) => {
          result.IsSuccess = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR moveGroup', e);
          return resolve(result);
        });
    });
  }

  /**
   * Получение кол-во пользователей команды текущего пользователя
   */
  getMyTeamCountEmployees() {
    return new Promise<ResponseModel<number>>(async (resolve) => {
      const result = new ResponseModel<number>();

      await this.api.Main.EmployeeService.getCountEmployeesUserAsync(
        mapGetMyTeamCountEmployeesRequest(CurrentUser.Id),
      )
        .then((resp) => {
          result.Value = resp.Count;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR getMyTeamCountEmployees', e);
          return resolve(result);
        });
    });
  }
}
